import React, { useEffect, useState } from 'react';
import { Copy, CornerDownRight, CornerLeftUp, Trash2 } from 'lucide-react';
import { FunctionalSelect, SelectItem } from '../ui/Select';
import Input from '../ui/Input';
import ColumnPicker, { TypeBadge } from './ColumnPicker';
import CriteriaDateValue, { formatCriteriaDateValue } from './CriteriaDateValue';
import MultiValueInput from './MultiValueInput';
import {
  RELATIVE_DATE_UNITS,
  canMoveOutOfGroup,
  columnLabel,
  conditionFieldErrors,
  conditionHasColumn,
  defaultOperatorForColumn,
  isFastIdScoreColumn,
  opIsInLast,
  opIsMulti,
  opLabel,
  opNeedsNoValue,
  operatorsForColumn,
  parseListValue,
  serializeListValue,
  typeFamily,
} from './criteriaUtils';

const FAST_ID_HELP =
  'Fast-ID score buckets profiles by days since their last signal: 10 = today, 9 = within 1 day, 8 = within 4, 7 = within 8, 6 = within 18, 5 = within 35, 4 = within 70, 3 = within 135, 2 = within 262, 1 = older, 0 = no signal.';

// The value control's minimum width lives on its flex wrapper, not on the control itself:
// a wrapper that can shrink to nothing lets the control overflow onto the row actions
// instead of wrapping to the next line. 14rem clears MultiValueInput's own 220px floor.
const VALUE_WRAP = 'min-w-[14rem] flex-1 basis-[14rem]';

const FastIdScoreInput = ({ value, onChange, className = '' }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <input
      type="range"
      min="0"
      max="10"
      step="1"
      value={/^\d+$/.test(String(value)) ? Number(value) : 5}
      onChange={(e) => onChange(String(e.target.value))}
      className="h-2 w-28 cursor-pointer accent-[#0062FB]"
      aria-label="Fast-ID score"
    />
    <input
      type="number"
      min="0"
      max="10"
      step="1"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="h-10 w-16 rounded-lg border border-input bg-surface px-2 text-center text-sm focus:outline-none focus:ring-1 focus:ring-[#18181B]/10"
      aria-label="Fast-ID score value"
    />
  </div>
);

/**
 * One criteria condition rendered as a sentence: [column] [operator] [value].
 */
const ConditionRow = ({ condition, path, onChange, onRemove, onDuplicate, onWrap, onMoveOut, autoOpenPicker = false }) => {
  const [pickerOpen, setPickerOpen] = useState(autoOpenPicker && !conditionHasColumn(condition));
  const hasColumn = conditionHasColumn(condition);
  const fam = typeFamily(condition.column_type);
  const isFastId = isFastIdScoreColumn(condition);
  const ops = operatorsForColumn(condition.column_type, condition.has_value_list, condition);
  const operator = ops.includes(condition.operator) ? condition.operator : ops[0];
  const errors = hasColumn ? conditionFieldErrors({ ...condition, operator }) : {};

  const update = (patch) => onChange(path, (node) => ({ ...node, ...patch }));

  // Keep the stored operator in step with what the row displays (e.g. an old segment whose
  // column gained a value list, or a fast_id_score condition saved with an operator we no longer offer).
  useEffect(() => {
    if (hasColumn && condition.operator !== operator) update({ operator });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasColumn, operator, condition.operator]);

  const applyColumn = (hit) => {
    const nextOp = defaultOperatorForColumn(hit);
    // A rule seeded from the offer Audience Studio without a column keeps
    // its picked value when the user chooses the column (eq/in only).
    const seededValue = !hasColumn && condition.offer_feature ? String(condition.value || '') : '';
    const keepSeeded = seededValue && (nextOp === 'eq' || nextOp === 'in');
    update({
      catalog: hit.catalog || '',
      schema: hit.schema || '',
      table: hit.table || '',
      column: hit.column || '',
      column_type: hit.column_type || '',
      has_value_list: Boolean(hit.has_value_list),
      display_name: columnLabel(hit),
      folder_list: Array.isArray(hit.folder_list) ? hit.folder_list : [],
      operator: nextOp,
      value: keepSeeded ? (nextOp === 'in' ? serializeListValue([seededValue]) : seededValue) : opIsInLast(nextOp) ? '30' : '',
      value2: opIsInLast(nextOp) ? 'days' : '',
    });
  };

  const setOperator = (nextOp) => {
    const patch = { operator: nextOp };
    const wasMulti = opIsMulti(operator);
    const isMulti = opIsMulti(nextOp);
    if (isMulti && !wasMulti) {
      const v = String(condition.value || '').trim();
      patch.value = v ? serializeListValue([v]) : '';
    } else if (wasMulti && !isMulti) {
      const list = parseListValue(condition.value);
      patch.value = list[0] || '';
    }
    if (opIsInLast(nextOp)) {
      if (!/^\d+$/.test(String(condition.value || ''))) patch.value = '30';
      if (!condition.value2 || !RELATIVE_DATE_UNITS.some((u) => u.value === condition.value2)) patch.value2 = 'days';
    } else if (opIsInLast(operator)) {
      patch.value = '';
      patch.value2 = '';
    }
    if (opNeedsNoValue(nextOp)) {
      patch.value = '';
      patch.value2 = '';
    }
    // Seed date pickers here (one update) rather than letting two pickers race on mount.
    if ((fam === 'date' || fam === 'timestamp') && !isFastId && !opNeedsNoValue(nextOp) && !opIsInLast(nextOp) && !opIsMulti(nextOp)) {
      const today = formatCriteriaDateValue(new Date(), fam);
      if (!String(patch.value ?? condition.value ?? '').trim()) patch.value = today;
      if (nextOp === 'between' && !String(patch.value2 ?? condition.value2 ?? '').trim()) patch.value2 = today;
    }
    update(patch);
  };

  const renderValue = () => {
    if (!hasColumn) return null;
    if (opNeedsNoValue(operator)) return null;

    if (isFastId) {
      return (
        <div className="flex flex-wrap items-center gap-2">
          <FastIdScoreInput value={condition.value} onChange={(v) => update({ value: v })} />
          {operator === 'between' ? (
            <>
              <span className="text-sm text-zinc-500">and</span>
              <FastIdScoreInput value={condition.value2} onChange={(v) => update({ value2: v })} />
            </>
          ) : null}
        </div>
      );
    }

    if (opIsMulti(operator)) {
      return (
        <MultiValueInput
          condition={condition}
          values={parseListValue(condition.value)}
          onChange={(list) => update({ value: serializeListValue(list) })}
          numeric={fam === 'numeric'}
          hasError={Boolean(errors.value)}
        />
      );
    }

    if (opIsInLast(operator)) {
      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min="1"
            step="1"
            inputMode="numeric"
            value={condition.value || ''}
            onChange={(e) => update({ value: e.target.value })}
            className={`w-24 ${errors.value ? 'border-red-300' : ''}`}
            aria-label="Amount"
          />
          <FunctionalSelect
            value={(condition.value2 || 'days').toLowerCase()}
            onValueChange={(v) => update({ value2: v })}
            className="w-32"
          >
            {RELATIVE_DATE_UNITS.map((u) => (
              <SelectItem key={u.value} value={u.value}>
                {u.label}
              </SelectItem>
            ))}
          </FunctionalSelect>
        </div>
      );
    }

    const single = (field) => {
      if (condition.has_value_list) {
        return (
          <MultiValueInput
            condition={condition}
            values={condition[field] ? [condition[field]] : []}
            onChange={(list) => update({ [field]: list[list.length - 1] || '' })}
            hasError={Boolean(errors[field])}
            single
          />
        );
      }
      if (fam === 'date' || fam === 'timestamp') {
        return (
          <CriteriaDateValue
            family={fam}
            value={condition[field] || ''}
            onChange={(v) => update({ [field]: v })}
            className="flex h-10 w-full rounded-lg border border-input bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#18181B]/10"
          />
        );
      }
      return (
        <Input
          type={fam === 'numeric' ? 'number' : 'text'}
          step={fam === 'numeric' ? 'any' : undefined}
          value={condition[field] || ''}
          onChange={(e) => update({ [field]: e.target.value })}
          placeholder={fam === 'numeric' ? '0' : 'Value'}
          className={errors[field] ? 'border-red-300' : ''}
          aria-label={field === 'value2' ? 'Second value' : 'Value'}
        />
      );
    };

    if (operator === 'between') {
      return (
        <div className="flex min-w-[14rem] flex-1 basis-[22rem] flex-wrap items-center gap-2">
          <div className="min-w-[10rem] flex-1 basis-[10rem]">{single('value')}</div>
          <span className="text-sm text-zinc-500">and</span>
          <div className="min-w-[10rem] flex-1 basis-[10rem]">{single('value2')}</div>
        </div>
      );
    }
    return <div className={VALUE_WRAP}>{single('value')}</div>;
  };

  const errorText = errors.value || errors.value2 || '';

  return (
    <div className="group/row relative rounded-lg border border-hairline bg-surface px-3 py-2.5 transition-shadow hover:shadow-sm">
      {/* Fields wrap within their own column so the actions can never be pushed off the card. */}
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {/* Column chip */}
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className={`inline-flex h-10 min-w-0 max-w-full items-center gap-2 rounded-lg border px-3 text-sm ${
              hasColumn
                ? 'border-hairline bg-[#FAFAF8] font-semibold text-[#18181B] hover:bg-[#F3F2EE]'
                : 'border-dashed border-[#0062FB]/50 bg-blue-50/40 font-medium text-[#0062FB] hover:bg-blue-50'
            }`}
          >
            <span className="min-w-0 truncate">
              {hasColumn
                ? columnLabel(condition)
                : condition.display_name
                  ? `${condition.display_name} = ${condition.offer_value_label || condition.value || '…'} — choose a column…`
                  : 'Choose a column…'}
            </span>
            {hasColumn ? <TypeBadge columnType={condition.column_type} /> : null}
          </button>

          {hasColumn ? (
            <FunctionalSelect
              value={operator}
              onValueChange={setOperator}
              className="w-auto min-w-[10rem]"
              triggerClassName="font-normal text-zinc-700"
            >
              {ops.map((op) => (
                <SelectItem key={op} value={op}>
                  {opLabel(op, isFastId ? 'int' : condition.column_type)}
                </SelectItem>
              ))}
            </FunctionalSelect>
          ) : null}

          {renderValue()}
        </div>

        {/* Row actions */}
        <div className="flex min-h-10 shrink-0 items-center gap-0.5 opacity-40 transition-opacity group-hover/row:opacity-100 focus-within:opacity-100">
          <button
            type="button"
            onClick={() => onWrap(path)}
            className="rounded p-1.5 text-zinc-400 hover:bg-[#F3F2EE] hover:text-[#18181B]"
            title="Wrap in a group (for or / and mixing)"
            aria-label="Wrap in group"
          >
            <CornerDownRight className="h-4 w-4" />
          </button>
          {canMoveOutOfGroup(path) ? (
            <button
              type="button"
              onClick={() => onMoveOut(path)}
              className="rounded p-1.5 text-zinc-400 hover:bg-[#F3F2EE] hover:text-[#18181B]"
              title="Move out to the group above"
              aria-label="Move out of group"
            >
              <CornerLeftUp className="h-4 w-4" />
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => onDuplicate(path)}
            className="rounded p-1.5 text-zinc-400 hover:bg-[#F3F2EE] hover:text-[#18181B]"
            title="Duplicate"
            aria-label="Duplicate condition"
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onRemove(path)}
            className="rounded p-1.5 text-zinc-400 hover:bg-pink-50 hover:text-[#F53D7A]"
            title="Remove"
            aria-label="Remove condition"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {isFastId && hasColumn ? <p className="mt-1.5 text-[11px] leading-snug text-zinc-400">{FAST_ID_HELP}</p> : null}
      {errorText ? (
        <p className="mt-1.5 text-xs text-red-600" role="alert">
          {errorText}
        </p>
      ) : null}

      <ColumnPicker isOpen={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={applyColumn} />
    </div>
  );
};

export default ConditionRow;
